import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Lock, 
  Key, 
  Settings, 
  User, 
  Mail, 
  Loader2, 
  Check, 
  ChevronLeft,
  Eye,
  EyeOff,
  ShieldAlert,
  ArrowRight
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import Modal from '../components/Modal';
import './StaffPermissions.css';

const StaffPermissions = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modals state
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isPermissionsModalOpen, setIsPermissionsModalOpen] = useState(false);
  
  // Selected targets
  const [selectedMember, setSelectedMember] = useState(null);
  
  // Password change states
  const [selectedUserForPass, setSelectedUserForPass] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  // Permissions configuration states
  const [selectedUserForPerms, setSelectedUserForPerms] = useState('');
  const [permissions, setPermissions] = useState({
    calendar: true,
    patients: true,
    accounting: false,
    staff: false,
    reports: true,
    settings: false
  });
  const [savingPerms, setSavingPerms] = useState(false);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('professionals')
        .select('*')
        .order('full_name');
      if (error) throw error;
      setStaff(data || []);
    } catch (err) {
      addNotification('Error al cargar personal', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // PASSWORD MODAL HANDLERS
  const handleOpenPasswordModal = (member) => {
    setSelectedMember(member);
    const users = member.users || [];
    if (users.length > 0) {
      setSelectedUserForPass(users[0].username);
    } else {
      setSelectedUserForPass('');
    }
    setNewPassword('');
    setShowPassword(false);
    setIsPasswordModalOpen(true);
  };

  const handleSavePassword = async () => {
    if (!selectedUserForPass) {
      addNotification('Selecciona un usuario válido', 'warning');
      return;
    }
    if (!newPassword.trim()) {
      addNotification('Ingresa la nueva contraseña', 'warning');
      return;
    }
    setSavingPassword(true);
    try {
      const updatedUsers = (selectedMember.users || []).map(u => {
        if (u.username === selectedUserForPass) {
          return { ...u, password: newPassword.trim() };
        }
        return u;
      });

      const { error } = await supabase
        .from('professionals')
        .update({ users: updatedUsers })
        .eq('id', selectedMember.id);

      if (error) throw error;
      addNotification('Contraseña modificada con éxito', 'success');
      setIsPasswordModalOpen(false);
      fetchStaff();
    } catch (err) {
      addNotification('Error al modificar contraseña: ' + err.message, 'error');
    } finally {
      setSavingPassword(false);
    }
  };

  // PERMISSIONS MODAL HANDLERS
  const handleOpenPermissionsModal = (member) => {
    setSelectedMember(member);
    const users = member.users || [];
    if (users.length > 0) {
      const initialUser = users[0];
      setSelectedUserForPerms(initialUser.username);
      // Load existing permissions if stored, otherwise default
      setPermissions(initialUser.permissions || {
        calendar: true,
        patients: true,
        accounting: false,
        staff: false,
        reports: true,
        settings: false
      });
    } else {
      setSelectedUserForPerms('');
      setPermissions({
        calendar: true,
        patients: true,
        accounting: false,
        staff: false,
        reports: true,
        settings: false
      });
    }
    setIsPermissionsModalOpen(true);
  };

  // When selected user changes inside Permissions modal, load their permissions
  const handleUserPermsChange = (username) => {
    setSelectedUserForPerms(username);
    const foundUser = (selectedMember?.users || []).find(u => u.username === username);
    if (foundUser) {
      setPermissions(foundUser.permissions || {
        calendar: true,
        patients: true,
        accounting: false,
        staff: false,
        reports: true,
        settings: false
      });
    }
  };

  const handleTogglePermission = (moduleKey) => {
    setPermissions(prev => ({
      ...prev,
      [moduleKey]: !prev[moduleKey]
    }));
  };

  const handleSavePermissions = async () => {
    if (!selectedUserForPerms) {
      addNotification('Selecciona un usuario válido', 'warning');
      return;
    }
    setSavingPerms(true);
    try {
      const updatedUsers = (selectedMember.users || []).map(u => {
        if (u.username === selectedUserForPerms) {
          return { ...u, permissions };
        }
        return u;
      });

      const { error } = await supabase
        .from('professionals')
        .update({ users: updatedUsers })
        .eq('id', selectedMember.id);

      if (error) throw error;
      addNotification('Permisos actualizados con éxito', 'success');
      setIsPermissionsModalOpen(false);
      fetchStaff();
    } catch (err) {
      addNotification('Error al guardar permisos: ' + err.message, 'error');
    } finally {
      setSavingPerms(false);
    }
  };

  return (
    <div className="permissions-page">
      {/* Header section with back button */}
      <div className="permissions-header-block">
        <button className="btn-back-staff" onClick={() => navigate('/staff')}>
          <ChevronLeft size={16} /> VOLVER AL STAFF
        </button>
        <div className="permissions-title-v4">
          <Lock size={28} className="icon-glow-orange" />
          <h1>Accesos y Permisos del Staff</h1>
        </div>
        <p className="permissions-header-subtext">
          Gestiona las credenciales de acceso, contraseñas y permisos específicos por módulo para todo el personal de la clínica.
        </p>
      </div>

      {/* Main content grid */}
      {loading ? (
        <div className="loading-container-p">
          <Loader2 className="animate-spin" size={48} />
          <p>Cargando lista de accesos del staff...</p>
        </div>
      ) : staff.length === 0 ? (
        <div className="no-staff-warning card glass-panel">
          <ShieldAlert size={48} className="text-orange" />
          <p>No se encontraron integrantes del staff registrados.</p>
        </div>
      ) : (
        <div className="permissions-grid">
          {staff.map((member) => {
            const users = member.users || [];
            return (
              <div key={member.id} className="permission-card card glass-panel">
                <div className="card-top-header">
                  <div className={`avatar-mini ${member.type === 'Profesional' ? 'pro' : 'admin'}`}>
                    <User size={16} />
                  </div>
                  <div className="member-meta">
                    <h4>{member.full_name?.toUpperCase()}</h4>
                    <span className={`role-badge ${member.type?.toLowerCase()}`}>
                      {member.type}
                    </span>
                  </div>
                </div>

                <div className="card-body-meta">
                  <div className="meta-row">
                    <Mail size={14} className="icon-muted" />
                    <span className="email-label" title={member.email}>
                      {member.email || 'Sin email registrado'}
                    </span>
                  </div>
                  
                  <div className="meta-accounts-box">
                    <span className="accounts-title">Cuentas con Acceso:</span>
                    {users.length === 0 ? (
                      <span className="no-accounts-text">Sin cuenta asignada (Sólo lectura/Inactivo)</span>
                    ) : (
                      <div className="accounts-tags-list">
                        {users.map((u, idx) => (
                          <span key={idx} className="account-tag" title={`Rol: ${u.role}`}>
                            {u.username} <small>({u.role})</small>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="card-actions-p">
                  <button 
                    className="btn-action-p password-btn" 
                    onClick={() => handleOpenPasswordModal(member)}
                    disabled={users.length === 0}
                    title={users.length === 0 ? "Primero debes crear una cuenta para este integrante en el Staff" : "Modificar contraseña"}
                  >
                    <Key size={14} /> CONTRASEÑA
                  </button>
                  
                  <button 
                    className="btn-action-p permissions-btn" 
                    onClick={() => handleOpenPermissionsModal(member)}
                    disabled={users.length === 0}
                    title={users.length === 0 ? "Primero debes crear una cuenta para este integrante en el Staff" : "Configurar accesos"}
                  >
                    <Settings size={14} /> PERMISOS
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL: Modify Password */}
      <Modal 
        isOpen={isPasswordModalOpen} 
        onClose={() => setIsPasswordModalOpen(false)} 
        title={`Modificar Contraseña: ${selectedMember?.full_name}`}
      >
        <div className="modal-config-form">
          <div className="form-group-p">
            <label>Seleccionar Usuario</label>
            <select 
              value={selectedUserForPass} 
              onChange={(e) => setSelectedUserForPass(e.target.value)}
            >
              {(selectedMember?.users || []).map((u, i) => (
                <option key={i} value={u.username}>{u.username} ({u.role})</option>
              ))}
            </select>
          </div>

          <div className="form-group-p" style={{ marginTop: '1.25rem' }}>
            <label>Nueva Contraseña</label>
            <div className="password-input-wrapper-p">
              <input 
                type={showPassword ? "text" : "password"} 
                placeholder="Escribe la nueva contraseña..." 
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <button 
                type="button" 
                className="btn-toggle-pass-visibility"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div className="form-footer-p" style={{ marginTop: '2rem' }}>
            <button className="btn-cancel-modal" onClick={() => setIsPasswordModalOpen(false)} disabled={savingPassword}>
              CANCELAR
            </button>
            <button className="btn-save-p" onClick={handleSavePassword} disabled={savingPassword || !newPassword.trim()}>
              {savingPassword ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              GUARDAR CONTRASEÑA
            </button>
          </div>
        </div>
      </Modal>

      {/* MODAL: Configure Permissions */}
      <Modal 
        isOpen={isPermissionsModalOpen} 
        onClose={() => setIsPermissionsModalOpen(false)} 
        title={`Accesos por Módulo: ${selectedMember?.full_name}`}
      >
        <div className="modal-config-form">
          <div className="form-group-p" style={{ marginBottom: '1.5rem' }}>
            <label>Seleccionar Usuario</label>
            <select 
              value={selectedUserForPerms} 
              onChange={(e) => handleUserPermsChange(e.target.value)}
            >
              {(selectedMember?.users || []).map((u, i) => (
                <option key={i} value={u.username}>{u.username} ({u.role})</option>
              ))}
            </select>
          </div>

          <h4 className="permissions-modal-subtitle">Permitir Acceso a Módulos:</h4>

          <div className="permissions-switches-list">
            <div className="switch-row" onClick={() => handleTogglePermission('calendar')}>
              <div className="switch-info">
                <span className="module-title">Agendas de Turnos y Calendario</span>
                <span className="module-desc">Visualizar, agendar, re-programar y cancelar turnos.</span>
              </div>
              <div className={`switch-toggle ${permissions.calendar ? 'on' : 'off'}`}>
                <div className="handle"></div>
              </div>
            </div>

            <div className="switch-row" onClick={() => handleTogglePermission('patients')}>
              <div className="switch-info">
                <span className="module-title">CRM de Pacientes e Historias Clínicas</span>
                <span className="module-desc">Crear pacientes, consultar datos de contacto y registrar evoluciones.</span>
              </div>
              <div className={`switch-toggle ${permissions.patients ? 'on' : 'off'}`}>
                <div className="handle"></div>
              </div>
            </div>

            <div className="switch-row" onClick={() => handleTogglePermission('accounting')}>
              <div className="switch-info">
                <span className="module-title">Contabilidad y Movimientos de Caja</span>
                <span className="module-desc">Visualizar balances, registrar ingresos/egresos y auditar transacciones.</span>
              </div>
              <div className={`switch-toggle ${permissions.accounting ? 'on' : 'off'}`}>
                <div className="handle"></div>
              </div>
            </div>

            <div className="switch-row" onClick={() => handleTogglePermission('staff')}>
              <div className="switch-info">
                <span className="module-title">Administración del Staff del Consultorio</span>
                <span className="module-desc">Modificar integrantes del personal, horarios y asignar consultorios.</span>
              </div>
              <div className={`switch-toggle ${permissions.staff ? 'on' : 'off'}`}>
                <div className="handle"></div>
              </div>
            </div>

            <div className="switch-row" onClick={() => handleTogglePermission('reports')}>
              <div className="switch-info">
                <span className="module-title">Listados, Informes y Auditorías</span>
                <span className="module-desc">Exportar bases de datos y auditar registros de logs borrados.</span>
              </div>
              <div className={`switch-toggle ${permissions.reports ? 'on' : 'off'}`}>
                <div className="handle"></div>
              </div>
            </div>

            <div className="switch-row" onClick={() => handleTogglePermission('settings')}>
              <div className="switch-info">
                <span className="module-title">Configuración del Sistema</span>
                <span className="module-desc">Ajustes generales, plantillas de email/WhatsApp e integración web.</span>
              </div>
              <div className={`switch-toggle ${permissions.settings ? 'on' : 'off'}`}>
                <div className="handle"></div>
              </div>
            </div>
          </div>

          <div className="form-footer-p" style={{ marginTop: '2rem' }}>
            <button className="btn-cancel-modal" onClick={() => setIsPermissionsModalOpen(false)} disabled={savingPerms}>
              CANCELAR
            </button>
            <button className="btn-save-p" onClick={handleSavePermissions} disabled={savingPerms}>
              {savingPerms ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              GUARDAR PERMISOS
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StaffPermissions;
